import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { clampDetectionRoiNorm } from "../../utils/detectionRoi";
import { containLayoutStyle, containRect } from "../../utils/videoContainCoords";
import "./poseOverlays.css";

const LEFT_COLOR = "#00BCD4";
const RIGHT_COLOR = "#FF4081";
const ROI_COLOR = "#FBBF24";

function normRect(a, b) {
  const x1 = Math.min(a.x, b.x);
  const y1 = Math.min(a.y, b.y);
  const x2 = Math.max(a.x, b.x);
  const y2 = Math.max(a.y, b.y);
  return clampDetectionRoiNorm({
    x: x1,
    y: y1,
    w: x2 - x1,
    h: y2 - y1,
  });
}

function pointerToContainNorm(event, svgEl, videoWidth, videoHeight) {
  if (!svgEl || typeof svgEl.createSVGPoint !== "function") {
    return { x: 0, y: 0, inside: false };
  }
  const ctm = svgEl.getScreenCTM();
  if (!ctm) return { x: 0, y: 0, inside: false };
  const vb = svgEl.viewBox?.baseVal;
  const boxW = Math.max(1, vb?.width || svgEl.clientWidth || 1);
  const boxH = Math.max(1, vb?.height || svgEl.clientHeight || 1);
  const rect = containRect(boxW, boxH, videoWidth, videoHeight);
  const pt = svgEl.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const local = pt.matrixTransform(ctm.inverse());
  const x = (local.x - rect.x) / Math.max(1e-6, rect.w);
  const y = (local.y - rect.y) / Math.max(1e-6, rect.h);
  return {
    x: Math.min(1, Math.max(0, x)),
    y: Math.min(1, Math.max(0, y)),
    inside: x >= -0.02 && x <= 1.02 && y >= -0.02 && y <= 1.02,
  };
}

/**
 * Preview auto detection ROI (blackout outside) or draw a manual box.
 */
export function DetectionRoiOverlay({
  proposedRoi = null,
  tableCorners = null,
  roiLeft = null,
  roiRight = null,
  videoWidth = 0,
  videoHeight = 0,
  contentTransform = null,
  toolbarContainer = null,
  confirming = false,
  onAccept,
  onConfirmManual,
  onBack,
  onCancel,
}) {
  const { t } = useTranslation();
  const stageRef = useRef(null);
  const svgRef = useRef(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const [mode, setMode] = useState("preview"); // preview | draw
  const [draft, setDraft] = useState(null);
  const [dragOrigin, setDragOrigin] = useState(null);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => {
      setStageSize({ w: el.clientWidth, h: el.clientHeight });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const layoutStyle = useMemo(
    () => containLayoutStyle(stageSize.w, stageSize.h, videoWidth, videoHeight),
    [stageSize, videoWidth, videoHeight]
  );

  const orientStyle = useMemo(() => {
    const base = { position: "absolute", inset: 0 };
    if (!contentTransform) return base;
    return {
      ...base,
      transform: contentTransform,
      transformOrigin: "center center",
    };
  }, [contentTransform]);

  const activeRoi = mode === "draw" ? draft : proposedRoi;
  const canAcceptProposed = Boolean(clampDetectionRoiNorm(proposedRoi));
  const canConfirmDraft = Boolean(clampDetectionRoiNorm(draft));

  const blackoutPath = useMemo(() => {
    const r = clampDetectionRoiNorm(activeRoi);
    if (!r) {
      return "M0,0 H1 V1 H0 Z";
    }
    return `M0,0 H1 V1 H0 Z M${r.x},${r.y} h${r.w} v${r.h} h${-r.w} Z`;
  }, [activeRoi]);

  const beginDraw = useCallback(() => {
    setMode("draw");
    setDraft(null);
    setDragOrigin(null);
  }, []);

  const cancelDraw = useCallback(() => {
    setMode("preview");
    setDraft(null);
    setDragOrigin(null);
  }, []);

  const onPointerDown = useCallback(
    (event) => {
      if (mode !== "draw" || confirming) return;
      const svg = svgRef.current;
      const p = pointerToContainNorm(event, svg, videoWidth, videoHeight);
      if (!p.inside) return;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setDragOrigin({ x: p.x, y: p.y });
      setDraft(null);
    },
    [mode, confirming, videoWidth, videoHeight]
  );

  const onPointerMove = useCallback(
    (event) => {
      if (mode !== "draw" || !dragOrigin || confirming) return;
      const svg = svgRef.current;
      const p = pointerToContainNorm(event, svg, videoWidth, videoHeight);
      setDraft(normRect(dragOrigin, p));
    },
    [mode, dragOrigin, confirming, videoWidth, videoHeight]
  );

  const onPointerUp = useCallback(
    (event) => {
      if (mode !== "draw" || !dragOrigin) return;
      const svg = svgRef.current;
      const p = pointerToContainNorm(event, svg, videoWidth, videoHeight);
      const next = normRect(dragOrigin, p);
      setDraft(next);
      setDragOrigin(null);
      try {
        event.currentTarget.releasePointerCapture?.(event.pointerId);
      } catch (_) {
        /* ignore */
      }
    },
    [mode, dragOrigin, videoWidth, videoHeight]
  );

  const toolbar = (
    <div className="table-calib-overlay__toolbar">
      <p className="table-calib-overlay__hint">
        {mode === "draw"
          ? t(
              "ballDetRoiDrawHint",
              "Drag a box over the play area. Everything outside will be blacked out for ball detection."
            )
          : t(
              "ballDetRoiPreviewHint",
              "Yellow box = detection ROI. Outside is blacked out. Accept or draw a tighter box to hide the other table."
            )}
      </p>
      <div className="table-calib-overlay__actions">
        <button
          type="button"
          className="table-calib-overlay__btn"
          onClick={onCancel}
          disabled={confirming}
        >
          {t("cancel", "Cancel")}
        </button>
        {mode === "preview" ? (
          <>
            <button
              type="button"
              className="table-calib-overlay__btn"
              onClick={onBack}
              disabled={confirming}
            >
              {t("back", "Back")}
            </button>
            <button
              type="button"
              className="table-calib-overlay__btn"
              onClick={beginDraw}
              disabled={confirming}
            >
              {t("ballDetRoiRedraw", "Draw manually")}
            </button>
            <button
              type="button"
              className="table-calib-overlay__btn table-calib-overlay__btn--primary"
              onClick={() => onAccept?.(proposedRoi)}
              disabled={confirming || !canAcceptProposed}
            >
              {confirming
                ? t("saving", "Saving…")
                : t("ballDetRoiAccept", "Accept ROI & detect")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="table-calib-overlay__btn"
              onClick={cancelDraw}
              disabled={confirming}
            >
              {t("back", "Back")}
            </button>
            <button
              type="button"
              className="table-calib-overlay__btn"
              onClick={() => {
                setDraft(null);
                setDragOrigin(null);
              }}
              disabled={confirming || !draft}
            >
              {t("ballDetRoiClear", "Clear")}
            </button>
            <button
              type="button"
              className="table-calib-overlay__btn table-calib-overlay__btn--primary"
              onClick={() => onConfirmManual?.(draft)}
              disabled={confirming || !canConfirmDraft}
            >
              {confirming
                ? t("saving", "Saving…")
                : t("ballDetRoiUseDrawn", "Use this ROI & detect")}
            </button>
          </>
        )}
      </div>
    </div>
  );

  const box = clampDetectionRoiNorm(activeRoi);

  return (
    <>
      <div
        className={`table-calib-overlay table-calib-overlay--draw${
          mode === "draw" ? " table-calib-overlay--drawing" : ""
        }`}
      >
        <div ref={stageRef} className="table-calib-overlay__stage">
          <div className="table-calib-overlay__orient" style={orientStyle}>
            <div className="table-calib-overlay__video-layer" style={layoutStyle}>
              <svg
                ref={svgRef}
                className="table-calib-overlay__svg"
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
                style={{
                  pointerEvents: mode === "draw" ? "auto" : "none",
                  touchAction: mode === "draw" ? "none" : undefined,
                  cursor: mode === "draw" ? "crosshair" : "default",
                }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              >
                <path
                  d={blackoutPath}
                  fill="rgba(0,0,0,0.72)"
                  fillRule="evenodd"
                  pointerEvents="none"
                />
                {Array.isArray(tableCorners) && tableCorners.length === 4 ? (
                  <polygon
                    points={tableCorners.map((c) => `${c.x},${c.y}`).join(" ")}
                    fill="rgba(255,220,80,0.08)"
                    stroke="rgba(255,220,80,0.55)"
                    strokeWidth="0.003"
                    pointerEvents="none"
                  />
                ) : null}
                {roiLeft ? (
                  <rect
                    x={roiLeft.x}
                    y={roiLeft.y}
                    width={roiLeft.w}
                    height={roiLeft.h}
                    fill={`${LEFT_COLOR}18`}
                    stroke={LEFT_COLOR}
                    strokeWidth="0.004"
                    strokeDasharray="0.012 0.008"
                    pointerEvents="none"
                  />
                ) : null}
                {roiRight ? (
                  <rect
                    x={roiRight.x}
                    y={roiRight.y}
                    width={roiRight.w}
                    height={roiRight.h}
                    fill={`${RIGHT_COLOR}18`}
                    stroke={RIGHT_COLOR}
                    strokeWidth="0.004"
                    strokeDasharray="0.012 0.008"
                    pointerEvents="none"
                  />
                ) : null}
                {box ? (
                  <rect
                    x={box.x}
                    y={box.y}
                    width={box.w}
                    height={box.h}
                    fill="none"
                    stroke={ROI_COLOR}
                    strokeWidth="0.007"
                    pointerEvents="none"
                  />
                ) : null}
              </svg>
            </div>
          </div>
        </div>
      </div>
      {toolbarContainer ? (
        createPortal(toolbar, toolbarContainer)
      ) : (
        <div className="table-calib-overlay__toolbar-fallback">{toolbar}</div>
      )}
    </>
  );
}

export default DetectionRoiOverlay;
